import { useEffect, useState } from "react";

/**
 * Live subscription to the user's `prefers-reduced-motion` setting.
 *
 * This is the SINGLE canonical site in the codebase that observes the
 * MediaQueryList. Read by App.tsx to compute the `reducedMotion` flag
 * passed into reducer actions (see chat-reducer.ts §answer/received).
 *
 * Per docs/frontend-react-guidelines.md §6.1, this is one of the narrow
 * sanctioned uses of `useEffect`: subscribing to a browser API with
 * proper cleanup.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  // Effect: syncs with the window's prefers-reduced-motion MediaQueryList. Cleanup: removes the change listener.
  // external: prefers-reduced-motion MediaQueryList — subscribed for live updates and cleanup on unmount.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event: MediaQueryListEvent): void => {
      setReduced(event.matches);
    };
    // Some older browsers expose `addListener` instead of `addEventListener`
    // — Vite's build target is es2022 so we use the modern API only.
    mql.addEventListener("change", onChange);
    setReduced(mql.matches);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, []);

  return reduced;
}
